import React, { useRef, useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Popconfirm,
  message,
  Tooltip,
  Card,
  Select,
  InputNumber,
  Spin,
  Tag,
  Switch,
} from 'antd';
import GET_ORG_DEAL_IDS from '../../../../core/GraphQl/Queries/GET_ORG_DEAL_IDS';
import GET_ORG_SALESFORCE_NAME from '../../../../core/GraphQl/Queries/GET_SALESFORCE_ACCOUNT_NAME';

// Locals
import { permissionsFromRoles } from '../../../../core/utils/dataManipulating/organization';
import SingleImageUploader from '../../../shared/images/SingleImageUploader';
import GroupedPermissionsToggler from './GroupedPermissionsToggler';
import ParentOrgIndicator from './ParentOrgIndicator';
import { errorType, tagDomainPattern } from '../../../../core/utils/constants/constants';
import { QuestionCircleOutlined, CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { MenuItemTextColor } from '../../../shared/globalStyling/styledText';
import { isEmpty } from 'lodash';
import ValidateTagDomainLoader from './OrganizationQueriesLoader';
import { orgLevelMenuItems, defaultCPM } from '../../../../core/utils/constants/constants';
import { useForm } from 'antd/lib/form/Form';
import { getTimezoneOptions } from '../../../shared/configs/timezone.configs';
const { Option } = Select;

const OrganizationDetails = ({
  createOrg,
  updateOrg,
  deleteOrg,
  disableOrg,
  enableOrg,
  onChange,
  onSwitchChange,
  selectedOrg,
  currentOrg,
  currentRole,
  organizations,
  preview,
  currentuser,
  setDisplayedOrganization,
  refetchOrganizations,
  createDspAdvertiser,
  createSalesforceForOrg,
}) => {
  // Fetch dealIds per-org on selection
  const { data: dealIdsData } = useQuery(GET_ORG_DEAL_IDS, {
    variables: { id: selectedOrg?.id },
    skip: !selectedOrg?.id,
    fetchPolicy: 'cache-and-network',
  });
  const dataDealIds = dealIdsData?.org?.dealIds || [];

  const { data: salesforceNameData, loading: loadingSalesforceName } = useQuery(
    GET_ORG_SALESFORCE_NAME,
    {
      variables: { salesforceAccountId: selectedOrg?.salesforceId },
      skip: !selectedOrg?.salesforceId,
      fetchPolicy: 'network-only',
    }
  );
  const salesforceAccountName = salesforceNameData?.getOrgSalesforceName || null;

  // Move ALL hooks to the top of the component - BEFORE any conditional logic
  const [error, setError] = useState('');
  const nameInput = useRef(null);
  const domainInput = useRef(null);
  const platformDomainInput = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submittedUrl, setSubmittedUrl] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [form] = useForm();
  const [domainError, setDomainError] = useState(false);
  const [creatingDspAdvertiser, setCreatingDspAdvertiser] = useState(false);
  const [priceSelected, setPriceSelected] = useState('CPM');

  // Extract selectedOrg ID for useEffect dependency to avoid ESLint warnings
  const selectedOrgId = selectedOrg?.id;

  useEffect(() => {
    form
      .validateFields(['domain'])
      .then(() => setDomainError(false))
      .catch(() => setDomainError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('domain')]);

  useEffect(() => {
    // Only update form fields when selectedOrg exists and we're switching organizations
    // or when validation result changes, not on every keystroke
    if (selectedOrg) {
      form.setFieldsValue({
        name: selectedOrg.name,
        description: selectedOrg.description,
        domain: selectedOrg.domain,
        platformDomain: selectedOrg.platformDomain,
        tagDomain: selectedOrg.tagDomain,
        level: selectedOrg.level,
        cpm: selectedOrg.cpm,
        rpm: selectedOrg.rpm,
        CPAcronym: selectedOrg.CPAcronym,
        zone: selectedOrg.zone || 'UTC',
        modelType: selectedOrg.modelType || 'CPM',
        videoCPM: selectedOrg.videoCPM,
        videoContractCPM: selectedOrg.videoContractCPM,
        displayCPM: selectedOrg.displayCPM,
        displayContractCPM: selectedOrg.displayContractCPM,
        premiumDisplayCPM: selectedOrg.premiumDisplayCPM,
        premiumDisplayContractCPM: selectedOrg.premiumDisplayContractCPM,
        tvOttStandardCPM: selectedOrg.tvOttStandardCPM,
        tvOttStandardContractCPM: selectedOrg.tvOttStandardContractCPM,
        tvOttPremiumCPM: selectedOrg.tvOttPremiumCPM,
        tvOttPremiumContractCPM: selectedOrg.tvOttPremiumContractCPM,
        percentage: selectedOrg.percentage,
        contractPercentage: selectedOrg.contractPercentage,
        contract: selectedOrg.contract,
        salesforceId: selectedOrg.salesforceId || '',
        billingEmail: selectedOrg.billingEmail || '',
      });
    }
  }, [form, selectedOrg, selectedOrgId, validationResult]);
  // Safe destructuring with defaults - will be empty objects if selectedOrg is null
  const {
    name = '',
    logo: _logo = null,
    signInLogo: _signInLogo = null,
    description = '',
    domain = '',
    level = 'STANDARD',
    cpm = '7',
    rpm = 0,
    id = null,
    features = {},
    parentOrg = [],
    config = {},
    dataConfig = {},
    platformDomain = '',
    zone = 'UTC',
    modelType = 'CPM',
    contract = false,
    displayCPM = '7',
    displayContractCPM = '7',
    videoCPM = '7',
    videoContractCPM = '7',
    premiumDisplayCPM = '12',
    premiumDisplayContractCPM = '12',
    tvOttStandardCPM = '35',
    tvOttStandardContractCPM = '35',
    tvOttPremiumCPM = '40',
    tvOttPremiumContractCPM = '40',
    percentage = '20',
    contractPercentage = '20',
    // Parent org's features/config - used when creating a new child org
    parentFeatures = null,
    parentConfig = null,
    salesforceId = '',
  } = selectedOrg || {};

  const tempConfig = {
    priceVisible: false,
    isAdmin: false,
    isPartner: false,
    isTrafficker: false,
    exportData: false,
    isSelfService: false,
    hasInsights: false,
    providersVisible: false,
    isProcessor: false,
    canResetPasswords: false,
    campaignSummary: false,
    isPacing: false,
    pastData: false,
    segment: false,
    articles: false,
    amplify: false,
    configurator: false,
    billing: false,
    sitesAppsTransactions: false,
    isBeta: false,
  };

  // Check if the only parent is MediaJelAdmin so that some config will be shown.
  const isMJParent = parentOrg.length <= 1 ? true : false;

  const { isPreviewOrg } = currentuser || {};
  const isMediaJelAdmin = currentRole?.org?.name === 'MediaJelAdmin';
  const isStandard = currentOrg?.level === 'STANDARD';

  const isDisabled = currentOrg?.name === name ? true : false;

  const orgIsEnabled = Object.keys(features).includes('ENABLED');

  const displayIsEnabled = Object.keys(features).includes('CAMPAIGNS');

  // Features - Determine which features are available based on context:
  // 1. If editing own org (currentOrg.id === id), use currentOrg.features
  // 2. If creating a new org (!id), use parentFeatures (parent org's features)
  // 3. If editing a child org (has id, has parentFeatures), use parentFeatures
  const permissionsRoles =
    currentOrg?.id === id ? currentOrg.features : parentFeatures || features || {};

  // Configuration - Same logic for config settings:
  // 1. If editing own org, use currentOrg.config
  // 2. If creating a new org, use parentConfig (parent org's config)
  // 3. If editing a child org (has id, has parentConfig), use parentConfig
  const permissionsConfig =
    currentOrg?.id === id ? currentOrg.config : parentConfig || config || tempConfig;

  const isAdmin = currentuser?.permission ? currentuser.permission.isAdmin : false;

  const isProcessor = currentOrg?.config ? currentOrg.config.isProcessor : false;

  const isSelfService = currentOrg?.config ? currentOrg.config.isSelfService : false;

  const orgLevel =
    currentOrg?.id === id
      ? currentOrg.level
      : selectedOrg && organizations
        ? organizations.find((org) => org.id === parentOrg[parentOrg.length - 1]?.id)?.level
        : 'STANDARD';
  const orgLevelSelection =
    orgLevel === 'PLATFORM' ? orgLevelMenuItems : [{ name: 'STANDARD', value: 'STANDARD' }];
  const priceModelSelection = [
    { name: 'CPM', value: 'CPM' },
    { name: 'Percentage', value: 'Percentage' },
  ];
  // const handleQueryResult = data => {
  //   setQueryData(data);
  // };

  const checkRules = async () => {
    try {
      if (!name || name.trim() === '') {
        message.error('Please enter a organization name');
        return false;
      }

      if (cpm >= 100) {
        message.error('Please enter a CPM Limit within 1-100');
        return false;
      }

      /*if (rpm > cpm) {
        message.error('Please enter an RPM that is lower or equal to CPM');
        return false;
      }*/

      if (platformDomain) {
        try {
          await validateDomainField(null, platformDomain);
        } catch (msg) {
          setError(typeof msg === 'string' ? msg : 'Invalid domain');
          return false;
        }
      }

      setError('');
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const validateDomainField = (_, value) => {
    if (!value) return Promise.resolve();

    if (/^(https?:\/\/|\/)/i.test(value)) {
      return Promise.reject("Please enter a domain without 'https://', 'http://', or '/'.");
    }

    if (value.startsWith('.') || value.endsWith('.')) {
      return Promise.reject('Domain cannot start or end with a dot.');
    }

    if (/\.\./.test(value)) {
      return Promise.reject('Domain cannot contain consecutive dots.');
    }

    if (value.length > 253) {
      return Promise.reject('Domain is too long (max 253 characters).');
    }

    const labels = value.split('.');

    if (labels.length < 2) {
      return Promise.reject('Please enter a valid domain (e.g., example.com).');
    }

    if (labels.length > 5) {
      return Promise.reject('Domain has too many levels (max 5).');
    }

    for (const label of labels) {
      if (label.length === 0 || label.length > 63) {
        return Promise.reject('Each part of the domain must be 1-63 characters.');
      }
      if (!/^[a-zA-Z0-9-]+$/.test(label)) {
        return Promise.reject('Domain parts can only contain letters, numbers, and hyphens.');
      }
      if (label.startsWith('-') || label.endsWith('-')) {
        return Promise.reject('Domain parts cannot start or end with a hyphen.');
      }
    }

    const tld = labels[labels.length - 1];
    if (!/^[a-zA-Z]{2,63}$/.test(tld)) {
      return Promise.reject('Domain must end with a valid TLD (e.g., .com, .net).');
    }

    return Promise.resolve();
  };

  /**
   * Create Organization function
   * handles creation of new organization and UI feedback
   * @param {*} values
   * @returns null
   */
  const handleSubmit = async (values) => {
    // e.preventDefault();
    setLoading(true);
    if (!(await checkRules())) {
      setLoading(false);
      return;
    }
    // If the selected customer already has an ID we know they are in the DB
    const salesforceIdValue = form.getFieldValue('salesforceId') || '';
    onChange('salesforceId', salesforceIdValue);

    message.loading('Action in progress..', 0);
    if (id) {
      updateOrg()
        .then(() => {
          message.destroy();
          message.success(`Organization ${name} successfully updated`);
          setLoading(false);
        })
        .catch((err) => {
          message.destroy();
          if (err === 'feature in empty') {
            message.error(`Please select at least one feature to update the organization!`);
          } else if (err === 'This name already exists') {
            message.error(`Error: Org ${name} already exists, please use a different name`);
          } else {
            message.error(`Error: ${name} not updated`);
          }
          console.log(err);
          setLoading(false);
        });
    } else {
      createOrg()
        .then(async () => {
          message.destroy();
          message.success(`Organization ${name} successfully created`);
          await refetchOrganizations({
            first: 300,
            skip: 0,
          });
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          message.destroy();
          if (err === errorType.nameDuplicate) {
            message.error(`Error: '${name}' Organization name already exists!`);
            // Focus org name input
            nameInput.current.focus();
          } else if (err === errorType.domainDuplicate) {
            message.error(`Error: '${domain}' Domain already used by other organization!`);
            // Focus domain input
            domainInput.current.focus();
          } else if (err === 'feature in empty') {
            message.error(`Please select at least one feature to create an organization!`);
          } else {
            message.error(`Error: ${name} not created`);
          }
          setLoading(false);
        });
    }
  };

  const onClickDelete = (e) => {
    e.stopPropagation();

    setLoading(true);
    message.loading('Action in progress..', 0);
    deleteOrg()
      .then(() => {
        message.destroy();

        message.success(`Organization ${name} successfully deleted`);
        setTimeout(() => {
          window.location.reload();
        }, 500);
        setLoading(false);
      })
      .catch((err) => {
        message.destroy();
        message.error(`Error: ${name} not deleted`);
        setLoading(false);
      });
  };

  const onClickDisable = async (e) => {
    e.stopPropagation();

    message.loading('Action in progress', 0);
    try {
      await disableOrg();
      // message.destroy();
      await refetchOrganizations({
        first: 0,
        skip: 0,
      });
      message.destroy();
      message.success(`Organization ${name} successfully disabled`);
    } catch (err) {
      message.destroy();
      message.error(`Error: ${name} not disabled`);
    }
  };

  const onClickEnabled = async () => {
    message.loading('Action in progress', 0);
    try {
      await enableOrg();
      // message.destroy();
      await refetchOrganizations({
        first: 0,
        skip: 0,
      });
      message.destroy();
      message.success(`Organization ${name} successfully enabled`);
    } catch (err) {
      message.destroy();
      message.error(`Error: ${name} not enabled`);
    }
  };

  const handleValidateClick = () => {
    form.validateFields().then((values) => {
      const url = values.tagDomain?.trim();
      setSubmittedUrl(url);
      setValidationResult(null);
    });
  };

  const handleResult = (data) => {
    setValidationResult(data);
  };

  const handleCreateAdvertiserInDSP = async () => {
    if (!selectedOrg?.id) {
      message.error('No organization selected');
      return;
    }

    setCreatingDspAdvertiser(true);
    message.loading('Creating advertiser...', 0);

    try {
      const result = await createDspAdvertiser({
        variables: {
          input: {
            orgId: selectedOrg.id,
            dspName: 'Nexxen',
          },
        },
      });

      message.destroy();

      if (result?.status === 'success' && result.data) {
        const advertiserData = result.data;

        if (advertiserData.message?.includes('already has a valid DSP')) {
          message.warning('A DSP advertiser ID is already set.');
        } else {
          message.success(
            `Advertiser created successfully! The DSP advertiser ID is now set to: ${advertiserData.dspAdvertiserId}`
          );

          if (advertiserData.dspAdvertiserId) {
            onChange('dspAdvertiserId', advertiserData.dspAdvertiserId, 'DATACONFIG');
          }
        }

        if (refetchOrganizations) {
          await refetchOrganizations();
        }
      } else {
        message.error('Failed to create advertiser - unexpected response');
      }
    } catch (error) {
      message.destroy();
      const errorMsg = error.message || 'Unknown error';
      message.error(`Error creating advertiser: ${errorMsg}`);
    } finally {
      setCreatingDspAdvertiser(false);
    }
  };

  const handleCreateSalesforceForOrg = async () => {
    if (!selectedOrg?.id) {
      message.error('No organization selected');
      return;
    }

    message.loading('Creating Salesforce Account...', 0);

    try {
      const result = await createSalesforceForOrg(selectedOrg.id);

      message.destroy();

      if (result?.data?.salesforceId) {
        message.success('Salesforce Account Created Successfully');
      } else {
        message.error('Failed to create Salesforce Account');
      }
    } catch (error) {
      message.destroy();
      message.error(`Error creating Salesforce Account: ${error.message || 'Unknown error'}`);
    }
  };

  // Show placeholder when no org is selected - MUST be after all hooks
  if (!selectedOrg) {
    return (
      <Card
        headStyle={{
          border: '0.5px solid #e8e8e8',
          background: '#FAFAFA',
        }}
        title="Details"
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>Please select an organization to view details.</p>
        </div>
      </Card>
    );
  }

  return (
    <Spin spinning={loading}>
      <Card
        headStyle={{
          border: '0.5px solid #e8e8e8',
          background: '#FAFAFA',
        }}
        title="Details"
        extra={
          <Row layout="vertical" type="flex" justify="end" gutter={10}>
            {id ? (
              <React.Fragment>
                {false && (
                  <Col>
                    <Popconfirm
                      title="This is permanent. Delete this organization?"
                      onConfirm={(e) => onClickDelete(e)}
                      okText="Yes"
                      cancelText="No"
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      disabled={isDisabled || loading}
                    >
                      <Button size="small" type="danger" disabled={isDisabled || loading}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Col>
                )}

                {isAdmin &&
                  (orgIsEnabled ? (
                    <Col>
                      <Popconfirm
                        title="Disable this organization?"
                        onConfirm={(e) => onClickDisable(e)}
                        okText="Yes"
                        cancelText="No"
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        disabled={isDisabled || loading}
                      >
                        <Button
                          size="small"
                          type="danger"
                          ghost={true}
                          disabled={isDisabled || loading}
                        >
                          Disable
                        </Button>
                      </Popconfirm>
                    </Col>
                  ) : (
                    <Col>
                      <Button
                        size="small"
                        type="primary"
                        disabled={isDisabled || loading}
                        style={{
                          borderColor: '#52c41a',
                          background: '#52c41a',
                        }}
                        onClick={onClickEnabled}
                      >
                        Enable
                      </Button>
                    </Col>
                  ))}

                <Col>
                  {currentOrg.id !== id && name !== '' ? (
                    <Tooltip
                      title={
                        isPreviewOrg
                          ? "You can't preview another org while already in preview mode."
                          : 'Assume Role'
                      }
                    >
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => preview()}
                        disabled={loading || isPreviewOrg}
                      >
                        Preview Advertiser
                      </Button>
                    </Tooltip>
                  ) : null}
                </Col>
                {isAdmin && (
                  <>
                    <Col>
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleSubmit}
                        disabled={loading || domainError}
                      >
                        Update
                      </Button>
                    </Col>
                  </>
                )}
              </React.Fragment>
            ) : null}
          </Row>
        }
      >
        <Form layout="vertical" form={form}>
          {/* Parent Organization Hierarchy Indicator */}
          <ParentOrgIndicator
            selectedOrg={selectedOrg}
            currentOrg={currentOrg}
            isCreating={!id}
            currentUserOrgId={currentuser?.role?.org?.id}
          />

          <Row type="flex" justify="space-around">
            <Col span={10} xs={22} xl={10}>
              <Form.Item
                name="name"
                label="Organization Name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input
                  disabled={isDisabled}
                  placeholder="organization name"
                  name="name"
                  onChange={({ target }) => {
                    onChange([target.name], target.value);
                  }}
                  ref={nameInput}
                />
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input
                  placeholder="optional description"
                  name="description"
                  onChange={({ target }) => {
                    onChange([target.name], target.value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={5} xs={22} sm={11} xl={4}>
              <Form.Item label="Logo">
                <SingleImageUploader onChange={onChange} image={_logo} statekey="logo" />
              </Form.Item>
            </Col>
            <Col span={5} xs={22} sm={11} xl={4}>
              {isMediaJelAdmin ? (
                <Form.Item label="Sign in logo" style={{ whiteSpace: 'nowrap' }}>
                  <SingleImageUploader
                    onChange={onChange}
                    image={_signInLogo}
                    statekey="signInLogo"
                  />
                </Form.Item>
              ) : null}
            </Col>
          </Row>

          <Row type="flex" justify="space-around">
            <Col span={10} xs={22} xl={10}>
              <Form.Item
                label="Advertiser Domain"
                name="domain"
                rules={[{ validator: validateDomainField }]}
              >
                <Input
                  name="domain"
                  onChange={({ target }) => {
                    onChange([target.name], target.value);
                  }}
                  ref={domainInput}
                  placeholder="example.com"
                />
              </Form.Item>
            </Col>
            <Col span={10} xs={22} xl={10}>
              <Form.Item label="Org Level" name="level">
                <Select
                  disabled={isDisabled}
                  defaultValue={'STANDARD'}
                  label="Org Level"
                  onSelect={(value) => {
                    onChange('level', value);
                  }}
                >
                  {orgLevelSelection.map((option) => {
                    return (
                      <Option key={option.value} value={option.value}>
                        <MenuItemTextColor text={option.name} />
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {(level === 'CHANNEL_PARTNER' || level === 'PLATFORM') && (
            <>
              <Row type="flex" justify="space-around">
                <Col span={10} xs={22} xl={10}>
                  <Form.Item
                    label="CP Acronym"
                    name="CPAcronym"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter an acronym',
                      },
                      {
                        max: 4,
                        message: 'Acronym must be at most 4 characters',
                      },
                      {
                        // Regex: Ensures the string contains only uppercase letters (A-Z) and digits (0-9).
                        pattern: /^[A-Z0-9]*$/,
                        message: 'Only uppercase letters and numbers are allowed',
                      },
                    ]}
                  >
                    <Input
                      name="CPAcronym"
                      onChange={({ target }) => {
                        // Convert to uppercase and remove invalid characters
                        // Regex pattern: /[^A-Z0-9]/g
                        // [^A-Z0-9] = Negated character class - matches anything NOT uppercase letters or digits
                        // g = Global flag - replace all occurrences, not just the first one
                        // This removes all lowercase letters, symbols, whitespace, and special characters
                        const cleanValue = target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        onChange([target.name], cleanValue);
                        // Update the form field value to reflect the cleaned input
                        form.setFieldsValue({ CPAcronym: cleanValue });
                      }}
                      placeholder="Enter acronym (max 4 chars)"
                      maxLength={4}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={10} xs={22} xl={10}>
                  <Form.Item
                    label="Platform Domain"
                    name="platformDomain"
                    validateStatus={error ? 'error' : ''}
                    help={error}
                    rules={[{ validator: validateDomainField }]}
                  >
                    <Tooltip title="Enter your domain (e.g., example.com)">
                      <Input
                        name="platformDomain"
                        onChange={({ target }) => {
                          onChange([target.name], target.value);
                        }}
                        ref={platformDomainInput}
                        placeholder="domain.com"
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          <Row
            type="flex"
            justify={level === 'CHANNEL_PARTNER' || level === 'PLATFORM' ? 'space-around' : 'start'}
          >
            {(level === 'CHANNEL_PARTNER' || level === 'PLATFORM') && (
              <Col span={10} xs={22} xl={10}>
                <Form.Item
                  label={
                    <>
                      Tag Domain{' '}
                      <Button size="small" style={{ marginLeft: 8 }} onClick={handleValidateClick}>
                        Verify Domain
                      </Button>
                    </>
                  }
                  name="tagDomain"
                  rules={[
                    {
                      pattern: tagDomainPattern,
                      message:
                        'Please enter a valid domain (e.g., https://example.com, example.com)',
                    },
                  ]}
                >
                  <Input
                    name="tagDomain"
                    onChange={({ target }) => {
                      onChange([target.name], target.value);
                    }}
                    ref={domainInput}
                  />
                </Form.Item>
              </Col>
            )}

            <Col
              span={10}
              xs={22}
              xl={10}
              style={level === 'STANDARD' ? { paddingLeft: '40px' } : {}}
            >
              <Form.Item label="Timezone" name="zone">
                <Select
                  placeholder="Select timezone"
                  showSearch
                  filterOption={(input, option) => {
                    if (!option || !option.children) return false;
                    const text = option.children.props?.text || '';
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                  onChange={(value) => onChange('zone', value)}
                >
                  {Object.entries(
                    getTimezoneOptions().reduce((groups, tz) => {
                      if (!groups[tz.region]) {
                        groups[tz.region] = [];
                      }
                      groups[tz.region].push(tz);
                      return groups;
                    }, {})
                  ).map(([region, timezones]) => (
                    <Select.OptGroup key={region} label={region}>
                      {timezones.map((tz) => (
                        <Option key={tz.key} value={tz.key}>
                          <MenuItemTextColor text={tz.displayLabel} />
                        </Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {submittedUrl && (
            <Row type="flex" justify="space-around">
              <Col span={10} xs={22} xl={10}>
                <ValidateTagDomainLoader url={submittedUrl} onResult={handleResult} />
                {validationResult && (
                  <div style={{ marginTop: 10 }}>
                    {validationResult.getTagsComparisonObject.isValid === true ? (
                      <p style={{ color: 'green' }}>Tag Domain is valid!</p>
                    ) : (
                      <p style={{ color: 'red' }}>Tag Domain is NOT valid.</p>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          )}

          {/* <Row type="flex" justify="start" style={{ paddingLeft: "40px" }}>
                <Col span={10} xs={22} xl={10}>
                  <Form.Item label="RPM">
                    <InputNumber
                      value={rpm}
                      disabled={isDisabled || isStandard}
                      onChange={target => {
                        onChange("rpm", target);
                      }}
                      min={1}
                    />
                  </Form.Item>
                </Col>
              </Row> */}

          <Row type="flex" justify="space-around">
            <Col span={10} xs={22} xl={10}>
              <Form.Item label="Pricing Model" name="modelType">
                <Select
                  defaultValue={modelType}
                  label="Pricing Model"
                  disabled={isDisabled}
                  onSelect={(value) => {
                    setPriceSelected(value);
                    onChange('modelType', value);
                  }}
                >
                  {priceModelSelection.map((option) => {
                    return (
                      <Option key={option.value} value={option.value}>
                        <MenuItemTextColor text={option.name} />
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={10} xs={22} xl={10}>
              <Form.Item label="With Contract" name="contract">
                <Switch
                  checked={contract}
                  name={`contract`}
                  size="default"
                  disabled={isDisabled}
                  onChange={(contract) => {
                    onChange('contract', contract);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row type="flex" justify="space-around">
            <Col span={10} xs={22} xl={10}>
              <Form.Item
                label={
                  <span>
                    Salesforce Account ID: {salesforceAccountName}{' '}
                    {selectedOrg?.salesforceId && (
                      <a
                        href={`https://mediajel.lightning.force.com/lightning/r/Account/${selectedOrg.salesforceId}/view`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkOutlined />
                      </a>
                    )}
                  </span>
                }
                name="salesforceId"
              >
                <Input
                  placeholder="Enter Salesforce ID"
                  name="salesforceId"
                  value={selectedOrg?.salesforceId}
                  onChange={({ target }) => {
                    onChange([target.name], target.value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col
              span={10}
              xs={22}
              xl={10}
              style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}
            >
              <Button
                type="primary"
                onClick={handleCreateSalesforceForOrg}
                disabled={selectedOrg?.salesforceId}
              >
                Create Salesforce Account
              </Button>
            </Col>
          </Row>

          <Row type="flex" justify="space-around">
            <Col span={10} xs={22} xl={10}>
              <Form.Item label="Billing Email" name="billingEmail">
                <Input
                  placeholder="Enter Billing Email"
                  name="billingEmail"
                  onChange={({ target }) => {
                    onChange([target.name], target.value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={10} xs={22} xl={10} />
          </Row>

          {modelType !== 'CPM' ? (
            <Row type="flex" justify={'start'}>
              {contract ? (
                <Col span={10} xs={22} xl={10} style={{ paddingLeft: '40px' }}>
                  <Form.Item
                    label="Contract Auction Plus Percentage"
                    name="contractPercentage"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter a percentage',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a percentage within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(contractPercentage || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('contractPercentage', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              ) : null}
              <Col
                span={10}
                xs={22}
                xl={10}
                style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
              >
                <Form.Item
                  label="Auction Plus Percentage"
                  name="percentage"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a percentage',
                    },
                    {
                      type: 'number',
                      min: 1,
                      max: 99,
                      message: 'Please enter a percentage within 1-99',
                    },
                  ]}
                >
                  <InputNumber
                    value={parseFloat(percentage || defaultCPM.cpm)}
                    onChange={(target) => {
                      onChange('percentage', parseFloat(target));
                    }}
                    disabled={isDisabled}
                    min={1}
                    max={99}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <>
              <Row type="flex" justify={'start'}>
                {contract ? (
                  <Col
                    span={10}
                    xs={22}
                    xl={10}
                    style={contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                  >
                    <Form.Item
                      label="Contract Standard Display CPM"
                      name="displayContractCPM"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter CPM',
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          message: 'Please enter a CPM Limit within 1-99',
                        },
                      ]}
                    >
                      <InputNumber
                        value={parseFloat(displayContractCPM || defaultCPM.cpm)}
                        onChange={(target) => {
                          onChange('displayContractCPM', parseFloat(target));
                        }}
                        disabled={isDisabled}
                        min={1}
                        max={99}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={10}
                  xs={22}
                  xl={10}
                  style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                >
                  <Form.Item
                    label="Standard Display CPM"
                    name="displayCPM"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter CPM',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a CPM Limit within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(displayCPM || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('displayCPM', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row type="flex" justify={'start'}>
                {contract ? (
                  <Col span={10} xs={22} xl={10} style={{ paddingLeft: '40px' }}>
                    <Form.Item
                      label="Contract Video Premium CPM"
                      name="videoContractCPM"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter CPM',
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          message: 'Please enter a CPM Limit within 1-99',
                        },
                      ]}
                    >
                      <InputNumber
                        value={parseFloat(videoContractCPM || defaultCPM.cpm)}
                        onChange={(target) => {
                          onChange('videoContractCPM', parseFloat(target));
                        }}
                        disabled={isDisabled}
                        min={1}
                        max={99}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={10}
                  xs={22}
                  xl={10}
                  style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                >
                  <Form.Item
                    label="Video Premium CPM"
                    name="videoCPM"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter CPM',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a CPM Limit within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(videoCPM || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('videoCPM', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row type="flex" justify={'start'}>
                {contract ? (
                  <Col span={10} xs={22} xl={10} style={{ paddingLeft: '40px' }}>
                    <Form.Item
                      label="Contract Premium Display CPM"
                      name="premiumDisplayContractCPM"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter CPM',
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          message: 'Please enter a CPM Limit within 1-99',
                        },
                      ]}
                    >
                      <InputNumber
                        value={parseFloat(premiumDisplayContractCPM || defaultCPM.cpm)}
                        onChange={(target) => {
                          onChange('premiumDisplayContractCPM', parseFloat(target));
                        }}
                        disabled={isDisabled}
                        min={1}
                        max={99}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={10}
                  xs={22}
                  xl={10}
                  style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                >
                  <Form.Item
                    label="Premium Display CPM"
                    name="premiumDisplayCPM"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter CPM',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a CPM Limit within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(premiumDisplayCPM || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('premiumDisplayCPM', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row type="flex" justify={'start'}>
                {contract ? (
                  <Col span={10} xs={22} xl={10} style={{ paddingLeft: '40px' }}>
                    <Form.Item
                      label="Contract Connected TV/OTT Standard CPM"
                      name="tvOttStandardContractCPM"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter CPM',
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          message: 'Please enter a CPM Limit within 1-99',
                        },
                      ]}
                    >
                      <InputNumber
                        value={parseFloat(tvOttStandardContractCPM || defaultCPM.cpm)}
                        onChange={(target) => {
                          onChange('tvOttStandardContractCPM', parseFloat(target));
                        }}
                        disabled={isDisabled}
                        min={1}
                        max={99}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={10}
                  xs={22}
                  xl={10}
                  style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                >
                  <Form.Item
                    label="Connected TV/OTT Standard CPM"
                    name="tvOttStandardCPM"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter CPM',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a CPM Limit within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(tvOttStandardCPM || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('tvOttStandardCPM', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row type="flex" justify={'start'}>
                {contract ? (
                  <Col span={10} xs={22} xl={10} style={{ paddingLeft: '40px' }}>
                    <Form.Item
                      label="Contract Connected TV/OTT Premium CPM"
                      name="tvOttPremiumContractCPM"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter CPM',
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          message: 'Please enter a CPM Limit within 1-99',
                        },
                      ]}
                    >
                      <InputNumber
                        value={parseFloat(tvOttPremiumContractCPM || defaultCPM.cpm)}
                        onChange={(target) => {
                          onChange('tvOttPremiumContractCPM', parseFloat(target));
                        }}
                        disabled={isDisabled}
                        min={1}
                        max={99}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={10}
                  xs={22}
                  xl={10}
                  style={!contract ? { paddingLeft: '40px' } : { paddingLeft: '120px' }}
                >
                  <Form.Item
                    label="Connected TV/OTT Premium CPM"
                    name="tvOttPremiumCPM"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter CPM',
                      },
                      {
                        type: 'number',
                        min: 1,
                        max: 99,
                        message: 'Please enter a CPM Limit within 1-99',
                      },
                    ]}
                  >
                    <InputNumber
                      value={parseFloat(tvOttPremiumCPM || defaultCPM.cpm)}
                      onChange={(target) => {
                        onChange('tvOttPremiumCPM', parseFloat(target));
                      }}
                      disabled={isDisabled}
                      min={1}
                      max={99}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {displayIsEnabled && (
            <Row type="flex" justify="start" style={{ paddingLeft: '40px' }}>
              <Col span={10} xs={22} xl={10}>
                <Form.Item
                  label={
                    <>
                      DSP Integration
                      {dataConfig?.dspAdvertiserId && (
                        <Tag
                          color="success"
                          icon={<CheckCircleOutlined />}
                          style={{ marginLeft: '8px' }}
                        >
                          Connected
                        </Tag>
                      )}
                      <Tooltip
                        title={
                          dataConfig?.dspAdvertiserId
                            ? `DSP Advertiser ID is already set. To create a new advertiser, preview this org and remove the existing DSP Advertiser ID first.`
                            : 'Click to create an advertiser in the DSP platform'
                        }
                      >
                        <QuestionCircleOutlined
                          style={{ color: '#1890ff', fontSize: '14px', marginLeft: '5px' }}
                        />
                      </Tooltip>
                    </>
                  }
                >
                  {dataConfig?.dspAdvertiserId ? (
                    <div>
                      <Input
                        value={dataConfig.dspAdvertiserId}
                        disabled
                        prefix={<LinkOutlined style={{ color: '#52c41a' }} />}
                        style={{
                          backgroundColor: '#f6ffed',
                          borderColor: '#b7eb8f',
                          color: '#389e0d',
                          fontWeight: 500,
                        }}
                      />
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                        This organization is connected to DSP Advertiser
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      onClick={handleCreateAdvertiserInDSP}
                      disabled={creatingDspAdvertiser}
                      loading={creatingDspAdvertiser}
                    >
                      Create Advertiser
                    </Button>
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row type="flex" justify="center">
            <Col span={22}>
              <GroupedPermissionsToggler
                loggedInPermissions={permissionsRoles}
                currentPermissions={features}
                config={!config ? {} : config}
                disabled={currentOrg.id === id}
                onSwitchChange={onSwitchChange}
                isAdmin={isMediaJelAdmin}
                isMJParent={isMJParent}
                isSelfService={isSelfService}
                isProcessor={isProcessor}
                orgTab={true}
                permissionsConfig={permissionsConfig}
              />
            </Col>
          </Row>
          {/* {isTrafficker ? (
            <Row type="flex" justify="center">
              <Col span={22}>
                <DataConfig
                  loggedInPermissions={dataConfig}
                  dataConfig={!dataConfig ? {} : dataConfig}
                  disabled={currentOrg.id === id}
                  onSwitchChange={onSwitchChange}
                  onChange={onChange}
                  hideFields={[
                    "tradeDeskIdentifier",
                    "currency",
                    "storageBucket",
                    "googleCustomerId",
                    "seo",
                    "semrushEnable",
                    "semrushConfig",
                    "attributionWindow",
                    "menuId",
                    "dspAdvertiserId",
                    "bing",
                    "googleAnalytics",
                    "tagProcessingConfig",
                    "paidSearchDataConfig",
                  ]}
                />
              </Col>
            </Row>
          ) : null} */}

          <Row type="flex" justify="end" gutter={10} style={{ marginRight: '100px' }}>
            {isAdmin &&
              (id ? (
                <React.Fragment>
                  <Col>
                    <Button type="primary" onClick={handleSubmit} disabled={loading || domainError}>
                      Update
                    </Button>
                  </Col>
                </React.Fragment>
              ) : (
                <Col>
                  <Button type="primary" onClick={handleSubmit}>
                    Create
                  </Button>
                </Col>
              ))}
          </Row>
        </Form>
      </Card>
    </Spin>
  );
};

export default React.memo(OrganizationDetails);
